import { MESApiService, spParameter } from "@dexteel/mesf-core";
import moment from "moment";
import type { PartInfo } from "../../../shared/models";
import type { PartId } from "../models/constants/parts";
import { SPARES_CONTAINER_TO_PART_TYPE } from "../models/constants/parts";
import type {
  PartsTreeRowAPI,
  PartsTreeResponse,
  PartDetailsMasterAPI,
  PartDetailsMetricsAPI,
  PartDetailsHistoryAPI,
  PartDetailsResponse,
} from "../models/api/types";

// Re-export API types for consumers that import from the repository
export type {
  PartsTreeRowAPI,
  ExpectedChildPosition,
  PartsTreeResponse,
  PartDetailsMasterAPI,
  PartDetailsMetricsAPI,
  PartDetailsHistoryAPI,
  PartDetailsResponse,
} from "../models/api/types";

// ============================================
// Repository Functions
// ============================================

/**
 * Get the parts tree for a specific root part.
 * Calls EQP.GetPartsTree directly — same approach as Roof/Sidewall.
 */
export const getPartsTree = async (rootPartId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "RootPartId", value: rootPartId });
  parameters.push({ name: "Timestamp", value: null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetPartsTree]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load parts tree",
    };
  }

  const data = response.data as PartsTreeResponse;

  return {
    ok: true,
    data,
  };
};

/**
 * Get the full offline parts tree (Caster-Offline with all mold children).
 * Uses optimized SP in RBD schema.
 */
export const getOfflinePartsTree = async () => {
  const apiService = new MESApiService();

  const response = await apiService.callV2(
    "[RBD].[GetCasterOfflinePartsTree]",
    [],
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load offline parts tree",
    };
  }

  const data = response.data as PartsTreeResponse;

  return {
    ok: true,
    data,
  };
};

/**
 * Get details for a specific part (includes metrics and history)
 * Calls RBD.GetCasterPartDetails stored procedure
 */
export const getPartDetails = async (
  partId: number | null,
  parentPartId?: number | null,
  positionId?: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Timestamp", value: null });
  parameters.push({ name: "ParentPartId", value: parentPartId ?? null });
  parameters.push({ name: "PositionId", value: positionId ?? null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetCasterPartDetails]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load part details",
    };
  }

  const data = response.data as PartDetailsResponse;

  // Process Master data dates (result set 0)
  data.tables[0].rows = data.tables[0].rows.map((row) => ({
    ...row,
    InstalledOn: row.InstalledOn ? moment.utc(row.InstalledOn).toDate() : null,
    ResetOn: row.ResetOn ? moment.utc(row.ResetOn).toDate() : null,
    UninstalledOn: row.UninstalledOn
      ? moment.utc(row.UninstalledOn).toDate()
      : null,
  }));

  // Process History data dates (result set 2)
  data.tables[2].rows = data.tables[2].rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    PreviousTask: row.PreviousTask
      ? moment.utc(row.PreviousTask).toDate()
      : null,
  }));

  return {
    ok: true,
    data,
  };
};

/**
 * Set a mold assembly online on a strand.
 * Uses the Caster-specific wrapper which also serves as the SignalR
 * invalidation source for CAS Data Entry cross-page refresh.
 */
export const setMoldOnline = async (
  moldPartId: number,
  strandPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "MoldPartId", value: moldPartId });
  parameters.push({ name: "StrandPartId", value: strandPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPCasterSetOnline]", parameters);
};

/**
 * Set a mold assembly offline (moves to spares).
 * Uses the Caster-specific wrapper which also serves as the SignalR
 * invalidation source for CAS Data Entry cross-page refresh.
 */
export const setMoldOffline = async (
  moldPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "MoldPartId", value: moldPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPCasterSetOffline]", parameters);
};

/**
 * Update part details
 */
export const upsertMoldPartDetails = async (
  partId: number,
  partInfo: PartInfo,
  comments: string | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Make", value: null });
  parameters.push({ name: "Model", value: null });
  parameters.push({ name: "Vendor", value: null });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({
    name: "WarehouseNumber",
    value: partInfo.WarehouseNumber || null,
  });
  parameters.push({
    name: "DrawingNumber",
    value: partInfo.DrawingNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", parameters);
};

/**
 * Install an existing spare part at a position.
 * Uses RBD.EQPInstallPart with @IsNewPart = 0.
 */
export const installExistingMoldPart = async (
  existingPartId: number,
  moldPartId: number,
  positionId: number,
  comments: string | null = null,
  operatorNumber?: number,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: false });
  parameters.push({ name: "PartId", value: existingPartId });
  parameters.push({ name: "ParentPartId", value: moldPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * INSTALL PART - Unified: handles both new part creation and existing spare install
 *
 * Uses RBD.EQPInstallPart which wraps CreatePart + UpsertTask + UpsertPartDetails
 * in a transaction with controlled timestamps.
 */
export const installMoldPart = async (
  moldPartId: number,
  positionId: number,
  partTypeId: number,
  partInfo: PartInfo,
  comments: string | null = null,
  operatorNumber?: number,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: true });
  parameters.push({ name: "PartTypeId", value: partTypeId });
  parameters.push({ name: "PartName", value: partInfo.SerialNumber || null });
  parameters.push({ name: "ParentPartId", value: moldPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({
    name: "WarehouseNumber",
    value: partInfo.WarehouseNumber || null,
  });
  parameters.push({
    name: "DrawingNumber",
    value: partInfo.DrawingNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * UNINSTALL PART
 * Uses RBD.EQPUninstallPart with chronological validation.
 */
export const uninstallMoldPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPUninstallPart]", parameters);
};

/**
 * REPAIR PART
 * Uses RBD.EQPRepairPart with chronological validation.
 */
export const repairMoldPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPRepairPart]", parameters);
};

/**
 * CREATE SPARE PART
 */
export const createSparePart = async (
  containerId: number,
  partInfo: PartInfo,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const partTypeId = SPARES_CONTAINER_TO_PART_TYPE[containerId as PartId];
  if (!partTypeId) {
    return { ok: false, message: "Invalid container ID" };
  }

  const partName = partInfo.PartName || partInfo.SerialNumber || null;

  const createParams: spParameter[] = [];
  createParams.push({ name: "PartTypeId", value: partTypeId });
  createParams.push({ name: "PartName", value: partName });
  createParams.push({ name: "UserId", value: userId });

  const createResponse = await apiService.callV2(
    "[RBD].[EQPCreatePart]",
    createParams,
  );

  if (!createResponse.ok) {
    return createResponse;
  }

  const newPartId = createResponse.data?.returnValue;

  if (!newPartId) {
    return { ok: false, message: "Failed to get new part ID" };
  }

  // Set part details
  const detailsParams: spParameter[] = [];
  detailsParams.push({ name: "PartId", value: newPartId });
  detailsParams.push({ name: "Make", value: null });
  detailsParams.push({ name: "Model", value: null });
  detailsParams.push({ name: "Vendor", value: null });
  detailsParams.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  detailsParams.push({
    name: "WarehouseNumber",
    value: partInfo.WarehouseNumber || null,
  });
  detailsParams.push({
    name: "DrawingNumber",
    value: partInfo.DrawingNumber || null,
  });
  detailsParams.push({ name: "InstallDate", value: null });
  detailsParams.push({ name: "Comments", value: null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", detailsParams);
};

/**
 * GET TASK METADATA
 */
export const getTaskMetadata = async (taskId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });

  const response = await apiService.callV2(
    "[RBD].[EQPGetTaskMetadata]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load task metadata",
    };
  }

  const data = response.data;
  if (data?.tables?.[0]?.rows) {
    data.tables[0].rows = data.tables[0].rows.map(
      (row: { CreatedAt: string | Date }) => ({
        ...row,
        CreatedAt: row.CreatedAt ? moment.utc(row.CreatedAt).toDate() : null,
      }),
    );
  }

  return {
    ok: true,
    data,
  };
};

/**
 * ADD TASK METADATA
 */
export const addTaskMetadata = async (
  taskId: number,
  notes: string,
  userId: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });
  parameters.push({ name: "Notes", value: notes });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[EQPAddTaskMetadata]", parameters);
};

/**
 * Get mold install/uninstall history for a specific strand.
 *
 * Calls EQPGetPartDetails in "location mode" (ParentPartId + PositionId)
 * WITHOUT @AssemblyPartTypeId so mold assemblies (PartTypeId=1003) are included.
 * The caster wrapper passes @AssemblyPartTypeId=1003 which filters molds OUT —
 * calling the base SP directly avoids that filter.
 */
export const getStrandMoldHistory = async (strandPartId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "ParentPartId", value: strandPartId });
  parameters.push({ name: "PositionId", value: 1 }); // Mold position on strand

  const response = await apiService.callV2(
    "[RBD].[EQPGetPartDetails]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load strand mold history",
    };
  }

  const data = response.data as PartDetailsResponse;

  // Process History data dates (result set 2)
  data.tables[2].rows = data.tables[2].rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    PreviousTask: row.PreviousTask
      ? moment.utc(row.PreviousTask).toDate()
      : null,
  }));

  return {
    ok: true,
    data,
  };
};

/**
 * DELETE SPARE PART
 */
export const deleteSparePart = async (
  partId: number,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[DeleteSparePart]", parameters);
};
